import React from 'react';
import { Check } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { MOBILE_UI } from '../../constants/mobileUi';

/**
 * Tappable option rows for use inside a `BottomSheet`.
 *
 * `options` accepts `{ value, label, hint? }` plus `{ header: true, label }` separators.
 * `onChange` always receives the tapped option value — in `multi` mode the parent owns
 * the toggle so it can enforce its own ordering and caps.
 */
const SheetOptionList = ({
  options = [],
  value,
  onChange,
  multi = false,
  max = null,
  testId,
  ariaLabel,
  getTestId,
}) => {
  const { colors } = useTheme();
  const selectedValues = multi ? (value || []) : [];
  const atCap = multi && max != null && selectedValues.length >= max;

  return (
    <div
      role={multi ? 'group' : 'radiogroup'}
      aria-label={ariaLabel}
      data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {options.map((option, index) => {
        if (option.header) {
          return (
            <div
              key={`header-${option.label}-${index}`}
              style={{
                padding: '14px 2px 6px',
                fontSize: MOBILE_UI.font.meta,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: colors.textMuted,
              }}
            >
              {option.label}
            </div>
          );
        }

        const selected = multi
          ? selectedValues.includes(option.value)
          : option.value === value;
        const disabled = Boolean(option.disabled) || (atCap && !selected);

        return (
          <button
            key={option.value}
            type="button"
            role={multi ? 'checkbox' : 'radio'}
            aria-checked={selected}
            aria-disabled={disabled || undefined}
            disabled={disabled}
            data-testid={getTestId ? getTestId(option) : undefined}
            onClick={() => onChange?.(option.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              width: '100%',
              minHeight: 48,
              padding: '10px 2px',
              border: 'none',
              borderBottom: `1px solid ${colors.borderLight}`,
              backgroundColor: 'transparent',
              color: selected ? colors.primary : colors.text,
              fontSize: MOBILE_UI.font.body,
              fontWeight: selected ? 700 : 500,
              textAlign: 'left',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.45 : 1,
            }}
          >
            <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </span>
              {option.hint ? (
                <span style={{ fontSize: MOBILE_UI.font.meta, fontWeight: 500, color: colors.textMuted }}>
                  {option.hint}
                </span>
              ) : null}
            </span>
            {selected ? <Check size={18} style={{ flexShrink: 0 }} /> : null}
          </button>
        );
      })}
    </div>
  );
};

export default SheetOptionList;
